import 'bootstrap/dist/css/bootstrap.min.css';

import { Spin, Modal, message } from 'antd';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { capitalize } from 'src/utils/capitalize';

import { designToolUrl } from '../globalSettings';
import UnitSelection from '../Components/AddProject/UnitSelection';
import BlueprintUpload from '../Components/AddProject/BlueprintUpload';
import { createProject, getParentProject } from '../services/projects';
// Import components
import ProjectTypeSelection from '../Components/AddProject/ProjectTypeSelection';
import DesignForm, { type DesignFormValues } from '../Components/AddProject/DesignForm';
import BlueprintForm, { type BlueprintFormValues } from '../Components/AddProject/BlueprintForm';

import type { Project, StyleInfo, ProjectType } from '../types/project';
import type { UnitType, CreateType } from '../Components/AddProject/types';
import { getProjectName } from './AutoRenderDashboard/utils';
import { useAuth } from 'src/AuthContext';
import { buildDimsParam, handleRedirectUrl } from 'src/utils/uiUtil';
import { openFromSelection } from 'src/utils/iframeNavigation';

const AddProjectNew: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const unitTypeParam = searchParams.get('unitType');
  // Only accept valid ProjectType values
  const type =
    typeParam === 'blueprint' || typeParam === 'design' ? (typeParam as ProjectType) : null;

  const unitType =
    unitTypeParam === 'single' || unitTypeParam === 'multi' ? (unitTypeParam as UnitType) : null;

  const [step, setStep] = useState<'projectTypeSelection' | 'unitSelection' | 'details' | 'upload'>(
    type ? (unitType ? 'details' : 'unitSelection') : 'projectTypeSelection',
  );

  const [selectedProjectType, setSelectedProjectType] = useState<ProjectType | null>(type);
  const [selectedUnitType, setSelectedUnitType] = useState<UnitType | null>(unitType);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [initialProject, setInitialProject] = useState<Project | null>(null);

  useEffect(() => {
    if (typeParam && unitTypeParam) {
      handleUnitSelection(unitType!);
    }
  }, [typeParam, unitTypeParam]);

  const handleUnitSelection = (selectedUnit: UnitType) => {
    setSelectedUnitType(selectedUnit);
    if (selectedProjectType === 'design') {
      setShowDetailsModal(true);
    } else {
      setStep('details');
    }
  };

  // Get parentProjectId from URL params
  const parentProjectId = searchParams.get('parentProjectId');
  const createType: CreateType = parentProjectId ? 'version' : 'project';

  if (createType === 'version' && !parentProjectId) {
    throw new Error('Parent project ID is required for creating a version.');
  }

  // Get today's date in YYYY-MM-DD format
  function getDefaultVersionName() {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }

  useEffect(() => {
    if (createType === 'version') {
      getParentProject(parentProjectId!)
        .then((projects) => {
          console.log('Fetched parent project:', projects);
          // If projects exist, find the most recently updated project
          if (Array.isArray(projects) && projects.length > 0) {
            const mostRecent = projects.reduce(
              (latest, project) =>
                new Date(project.updatedAt ?? '') > new Date(latest.updatedAt ?? '')
                  ? project
                  : latest,
              projects[0],
            );
            setInitialProject(mostRecent);
          } else {
            throw new Error('Parent project has no versions.');
          }
        })
        .catch((error) => {
          console.error('Error fetching parent project:', error);
          message.error('Failed to load parent project details.');
        });
    }
  }, [parentProjectId]);

  const handleProjectTypeSelection = (projectType: ProjectType) => {
    setSelectedProjectType(projectType);
    setStep('unitSelection');
  };

  const handleDesignSubmit = async (values: DesignFormValues) => {
    setLoading(true);
    try {
      const isMF = values.projectScope !== undefined;
      const styleInfo: StyleInfo | undefined = isMF
        ? {
            color: values.color,
            handleColor: values.handleColor,
            handleStyle: values.handleStyle,
            rodColor: values.rodColor,
            drawerColor: values.drawerColor,
            drawerStyle: values.drawerStyle,
            doorColor: values.doorColor,
            doorStyle: values.doorStyle,
            includeLegs: values.includeLegs,
            includeShelf: values.includeShelf,
            includeTopDoors: values.includeTopDoors,
            includeBottomDoors: values.includeBottomDoors,
          }
        : undefined;
      const isCreatingVersion = createType === 'version';

      const customerName = values.customerName || 'MC';

      const projectName =
        values.projectName ||
        (isCreatingVersion ? initialProject?.name : getProjectName(user?.email || ''));
      const payload = {
        projectType: selectedProjectType!,
        isMultiUnit: selectedUnitType === 'multi',
        name: projectName,
        versionName: values.versionName || getDefaultVersionName(),
        unitName: undefined,
        customer: values.customerEmail || undefined,
        productLine: values.productLine,
        salesforceId: values.salesforceId || undefined,
        customerName,

        isPostFieldMeasure: values.isPostFieldMeasure ?? false,
        ...(values.isPostFieldMeasure && {
          unitNumbersToInclude: values.unitNumbersToInclude ?? [],
        }),

        ...(isMF && {
          projectScope: values.projectScope,
          projectScopeCustom:
            values.projectScope?.toLowerCase() === 'custom' ? values.customProjectScope : undefined,
          styleInfo,
        }),
        ...(createType === 'version' && { parentProjectId }),
      };
      const data = await createProject(payload, user);

      message.success('Project created successfully!');
      setShowDetailsModal(false);

      // Redirect to design tool with project ID, carrying any wall
      // measurements Salesforce put on the link so the new closet opens with
      // the customer's room already built.
      const dimsParam = buildDimsParam(searchParams);
      const redirectUrl = handleRedirectUrl(user);
      if (redirectUrl) {
        openFromSelection(`${redirectUrl}/design?project_id=${data.projectId}${dimsParam}`);
        return;
      } else {
        openFromSelection(`${designToolUrl}/?project_id=${data.projectId}${dimsParam}`);
      }
    } catch (error) {
      console.error('Error creating project:', error);
      message.error('Failed to create project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBlueprintSubmit = async (values: BlueprintFormValues) => {
    setLoading(true);
    try {
      // Create StyleInfo object from form values
      const styleInfo: StyleInfo = {
        color: values.color,
        handleColor: values.handleColor,
        handleStyle: values.handleStyle,
        rodColor: values.rodColor,
        drawerColor: values.drawerColor,
        drawerStyle: values.drawerStyle,
        doorColor: values.doorColor,
        doorStyle: values.doorStyle,
        includeLegs: values.includeLegs,
        includeShelf: values.includeShelf,
        includeTopDoors: values.includeTopDoors,
        includeBottomDoors: values.includeBottomDoors,
      };

      // Prepare payload
      const isCreatingVersion = createType === 'version';

      const customerName = values.customerName || 'Default';

      const projectName =
        values.projectName ||
        (isCreatingVersion ? initialProject?.name : getProjectName(user?.email || ''));
      const payload = {
        name: projectName,
        versionName: values.versionName || getDefaultVersionName(),
        projectType: 'blueprint' as ProjectType,
        postMeasure: false,
        customer: values.customerEmail,
        productLine: values.productLine,
        projectScope: values.projectScope,
        projectScopeCustom: values.customProjectScope,
        styleInfo,
        isMultiUnit: selectedUnitType === 'multi',
        salesforceId: values.salesforceId || undefined,
        customerName,
        ...(createType === 'version' && { parentProjectId }),
      };

      console.log(payload);

      const data = await createProject(payload, user);

      setProjectId(data.projectId);
      message.success('Project created successfully!');
      setStep('upload');
    } catch (error) {
      console.error('Error creating project:', error);
      message.error('Failed to create project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !showDetailsModal && step !== 'details') {
    return (
      <div className='container py-4 text-center'>
        <Spin size='large' />
      </div>
    );
  }

  return (
    <>
      {step === 'projectTypeSelection' && (
        <ProjectTypeSelection
          onProjectTypeSelect={handleProjectTypeSelection}
          onCancel={() => navigate('/')}
          createType={createType}
        />
      )}

      {step === 'unitSelection' && selectedProjectType && (
        <UnitSelection
          onUnitSelect={handleUnitSelection}
          onCancel={() => navigate('/')}
          createType={createType}
        />
      )}

      {step === 'details' && selectedProjectType === 'blueprint' && selectedUnitType && (
        <BlueprintForm
          onSubmit={handleBlueprintSubmit}
          loading={loading}
          createType={createType}
          initialStyleInfo={initialProject?.styleInfo || null}
        />
      )}

      {step === 'upload' && projectId && (
        <BlueprintUpload projectId={projectId} onCancel={() => navigate('/')} />
      )}

      {/* Design Flow Modal */}
      <Modal
        title={`Create ${capitalize(createType)}`}
        open={showDetailsModal}
        onCancel={() => {
          setShowDetailsModal(false);
          setStep('unitSelection');
        }}
        footer={null}
        width={500}
      >
        <DesignForm
          onSubmit={handleDesignSubmit}
          loading={loading}
          unitType={selectedUnitType || 'single'}
          createType={createType}
        />
      </Modal>
    </>
  );
};

export default AddProjectNew;
